import React, { useState, useEffect } from 'react';
import Classes from '../Dashboard.module.css';
import { CalenderSimpleIcon, CalenderCancelIcon, MessageIcon, ProfileIcon } from '../../../../../../utils/Icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import backend from '../../../../../../api';

export default function Hero1({ bookings }) {
  const navigateTo = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- helpers ----
  const toLocalDateFromBooking = (b) => {
    if (b?.date && b?.start) {
      const [y, m, d] = String(b.date).split('-').map(Number);
      const [hh, mm] = String(b.start).split(':').map(Number);
      return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    }
    if (b?.startsIn) {
      const [datePart, timePart = "00:00"] = String(b.startsIn).split(" at ").map((s) => s.trim());
      const [y, m, d] = datePart.split("-").map(Number);
      const [hh, mm] = timePart.split(":").map(Number);
      return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    }
    return null;
  };

  const isFutureAndUpcoming = (b) => {
    const allow = new Set(["paid", "pending", "accepted"]);
    const label = (b?.status?.label || b?.status || "").toString().toLowerCase();
    if (!allow.has(label)) return false;
    const dt = toLocalDateFromBooking(b);
    return !!dt && dt.getTime() >= Date.now();
  };

  const formatNext = (dt) => {
    try {
      return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // ---- unread conversations (count ONLY if seen is explicitly false & sender != me) ----
  async function fetchUnreadMessages() {
    try {
      const { data } = await backend.get(`/api/chat/conversation?userId=${user._id}`);
      const conversations = data?.data || [];
      const myId = String(user._id);

      const unreadThreads = conversations.reduce((count, convo) => {
        // Prefer server-provided aggregate if present (e.g., convo.unseenCount)
        if (typeof convo?.unseenCount === 'number') {
          return count + (convo.unseenCount > 0 ? 1 : 0);
        }

        const last = convo?.last;
        if (!last) return count;

        // Normalize sender id
        const lastSender = String(
          (typeof last.sender === 'object' ? (last.sender?._id || last.sender?.id) : last.sender) || ''
        );

        // Only treat as unread if the API EXPLICITLY says seen === false
        const hasSeen = Object.prototype.hasOwnProperty.call(last, 'seen');
        const explicitlyUnseen = hasSeen && last.seen === false;

        if (explicitlyUnseen && lastSender !== myId) {
          return count + 1;
        }
        return count;
      }, 0);

      setUnreadMessages(unreadThreads);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError(err?.response?.data?.message || "Failed to load messages");
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user._id && user.role === "mentor") {
      fetchUnreadMessages();
    }
  }, [user._id, user.role]);

  // upcoming & canceled counts
  const futureBookings = (Array.isArray(bookings) ? bookings : []).filter(isFutureAndUpcoming);
  const upcomingMeetings = futureBookings.length;
  const nextBookingDt = futureBookings
    .map((b) => ({ b, dt: toLocalDateFromBooking(b) }))
    .filter(({ dt }) => !!dt)
    .sort((a, b) => a.dt - b.dt)[0]?.dt || null;

  const canceledMeetings = (Array.isArray(bookings) ? bookings : [])
    .filter((b) => (b?.status?.label || b?.status || "").toString().toLowerCase() === "canceled")
    .length;

  

  return (
    <div className={Classes.hero1}>
      {/* Upcoming Meetings (future only) */}
      <div className={Classes.heroBox}>
        <div className={Classes.hero1Img}>
          <CalenderSimpleIcon height="27px" width="27px" fill="white" />
        </div>
        <div>
          <h5 className={Classes.title1}>Upcoming Meetings</h5>
          <h3 className="pointer" onClick={() => navigateTo('/meetings')}>
            {loading ? '...' : upcomingMeetings} <span>&gt;</span>
          </h3>
          {/* <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {nextBookingDt ? `Next: ${formatNext(nextBookingDt)}` : 'No upcoming'}
          </div> */}
        </div>
      </div>

      <div className={Classes.miniLine}></div>

      {/* Canceled */}
      <div className={Classes.heroBox}>
        <div className={Classes.hero1Img}>
          <CalenderCancelIcon height="27px" width="27px" fill="white" />
        </div>
        <div>
          <h5 className={Classes.title1}>Meetings Canceled</h5>
          <h3 className="pointer" onClick={() => navigateTo('/meetings')}>
            {loading ? '...' : canceledMeetings} <span>&gt;</span>
          </h3>
        </div>
      </div>

      <div className={Classes.miniLine}></div>

      {/* Messages (unread threads) */}
      <div className={Classes.heroBox}>
        <div className={Classes.hero1Img}>
          <MessageIcon height="27px" width="27px" fill="white" />
        </div>
        <div>
          <h5 className={Classes.title1}>Messages</h5>
          <h3 className="pointer" onClick={() => navigateTo('/messages')}>
            {loading ? '...' : unreadMessages} Unread <span>&gt;</span>
          </h3>
        </div>
      </div>

      <div className={Classes.miniLine}></div>

      {/* Profile */}
      <div className={Classes.heroBox}>
        <div className={Classes.hero1Img}>
          <ProfileIcon height="27px" width="27px" fill="white" />
        </div>
        <div>
          <h5 className={Classes.title1}>Edit Profile</h5>
          <div className={Classes.flexAlign5}>
            <h3 className="pointer" onClick={() => navigateTo('/profile')}>
              Profile <span>&gt;</span>
            </h3>
          </div>
        </div>
      </div>
    </div>
  );
}
